import { Controller } from '@hotwired/stimulus';

export default class AddressAutocompleteController extends Controller {
  static targets = ['container', 'streetAddress', 'streetAddress2', 'city', 'postcode'];

  connect() {
    this.initialized = false;
    this.googleReady = typeof google !== 'undefined';

    if (!this.googleReady) {
      this.boundGoogleReady = () => {
        this.googleReady = true;
        this.tryInit();
      };
      document.addEventListener('google-maps-ready', this.boundGoogleReady, { once: true });
    }

    const widget = this.element.querySelector('.address-autocomplete-widget');
    if (widget) {
      this.observer = new MutationObserver(() => this.tryInit());
      this.observer.observe(widget, { attributes: true, attributeFilter: ['style'] });
    }

    this.tryInit();
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
    if (this.boundGoogleReady) document.removeEventListener('google-maps-ready', this.boundGoogleReady);
    this.selectedPlace = null;
  }

  tryInit() {
    if (this.initialized) return;
    if (!this.googleReady) return;
    const widget = this.element.querySelector('.address-autocomplete-widget');
    if (!widget || widget.style.display === 'none') return;
    this.initAutocomplete();
  }

  async initAutocomplete() {
    this.initialized = true;
    const { PlaceAutocompleteElement } = await google.maps.importLibrary('places');
    this.placeElement = new PlaceAutocompleteElement({
      includedRegionCodes: ['gb'],
      types: ['address'],
    });
    this.placeElement.classList.add('address-autocomplete-widget__element');
    this.containerTarget.appendChild(this.placeElement);


    this.placeElement.addEventListener('gmp-select', async (event) => {
      const prediction = event.placePrediction;
      if (!prediction) return;
      const place = prediction.toPlace();
      try {
        await place.fetchFields({ fields: ['addressComponents'] });
        this.selectedPlace = place;
      } catch (e) {
        console.error('[ac] fetchFields error:', e);
      }
    });
  }

  async fillAddress(event) {
    event.preventDefault();
    // Stop the click reaching the delegated handler in forms.js, which would run a full
    // form validation and scroll the user back up to the first not-yet-filled field.
    event.stopPropagation();
    if (!this.selectedPlace?.addressComponents) return;

    const components = this.selectedPlace.addressComponents;
    const get = (type) => components.find((c) => c.types.includes(type))?.longText || '';
    const getShort = (type) => components.find((c) => c.types.includes(type))?.shortText || '';

    const streetNumber = get('street_number');
    const route = get('route');
    const subpremise = get('subpremise');
    const premise = get('premise');

    this.streetAddressTarget.value = [streetNumber, route].filter(Boolean).join(' ') || premise;
    if (this.hasStreetAddress2Target) this.streetAddress2Target.value = subpremise;
    this.cityTarget.value = get('postal_town') || get('locality');
    this.postcodeTarget.value = getShort('postal_code');

    if (this.placeElement) this.placeElement.value = '';
    this.selectedPlace = null;
  }
}
